<script setup lang="ts">
import { computed } from 'vue'
import { AssignmentStatus } from '@/types'
import type { Assignment, Team, Personnel } from '@/types'
import FosSelect from '@/components/ui/FosSelect.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'
import { usePersonnelStore } from '@/stores/personnel'
import TeamTooltip from '@/components/teams/TeamTooltip.vue'
import { useDragState } from '@/composables/useDragState'
import draggable from 'vuedraggable'

const props = defineProps<{
  assignment: Assignment
  assignedTeams: Team[]
  canEdit: boolean
  permissionRole?: string | null
  sortable?: boolean
}>()

const emit = defineEmits<{
  dropTeam: [teamId: string]
  dropSecondary: [teamId: string]
  detachTeam: [teamId: string]
  statusChange: [status: AssignmentStatus]
  select: []
}>()

const { isDragging } = useDragState()

// ── Drop handler ──────────────────────────────────────────────────────────

function handleChange(event: { added?: { element: Team }; removed?: { element: Team } }) {
  if (event.added) {
    // assignedTeams hasn't updated yet, so length reflects the count before the drop
    if (props.assignedTeams.length === 0) emit('dropTeam', event.added.element.team_id)
    else emit('dropSecondary', event.added.element.team_id)
  }
  if (event.removed) emit('detachTeam', event.removed.element.team_id)
}

// ── Status styling ────────────────────────────────────────────────────────

const statusDotColor = computed(() => {
  const map: Record<string, string> = {
    [AssignmentStatus.Draft]: 'bg-amber-400',
    [AssignmentStatus.NotStarted]: 'bg-outline-variant',
    [AssignmentStatus.InProgress]: 'bg-blue-500',
    [AssignmentStatus.Completed]: 'bg-green-500',
  }
  return map[props.assignment.assignment_status] ?? 'bg-outline-variant'
})

const statusOptions = [
  { label: 'Draft', value: AssignmentStatus.Draft },
  { label: 'Not Started', value: AssignmentStatus.NotStarted },
  { label: 'In Progress', value: AssignmentStatus.InProgress },
  { label: 'Completed', value: AssignmentStatus.Completed },
]
</script>

<template>
  <div
    class="@container bg-surface rounded-sm cursor-pointer overflow-hidden select-none border border-border flex flex-col h-full"
    @click="emit('select')"
  >
    <!-- Header: name · CT badge · status -->
    <div class="flex items-center gap-1.5 px-2.5 pt-2 pb-2" @click.stop>
      <span
        v-if="sortable"
        class="drag-handle material-symbols-outlined text-[16px] text-outline shrink-0 cursor-grab active:cursor-grabbing"
        title="Drag to reorder"
        @click.stop
      >drag_indicator</span>
      <div class="flex items-center gap-1.5 min-w-0 flex-1">
        <h3 class="font-headline font-black text-base uppercase tracking-tight text-on-surface truncate">
          {{ assignment.assignment_name }}
        </h3>
        <img
          v-if="assignment.caltopo_assignment_id"
          src="/assets/caltopologo.svg"
          alt="CalTopo"
          class="shrink-0 w-4 h-4 hidden @[150px]:block"
        />
      </div>
      <PermissionGuard action="assignment.create" :role="permissionRole">
        <div class="ml-auto flex items-center gap-1 shrink-0">
          <span class="w-1.5 h-1.5 shrink-0" :class="statusDotColor" />
          <FosSelect
            :model-value="assignment.assignment_status"
            :options="statusOptions"
            trigger-class="p-0 leading-none flex items-center text-[11px] font-mono uppercase tracking-wide text-on-surface-variant cursor-pointer focus:outline-none hover:text-on-surface"
            icon-class="hidden"
            @update:model-value="emit('statusChange', $event as AssignmentStatus)"
          />
        </div>
      </PermissionGuard>
    </div>

    <!-- Info section — always rendered so flex-1 acts as a spacer when empty -->
    <div class="border-t border-border px-2.5 py-2 flex flex-col gap-1.5 flex-1">
      <div v-if="assignment.priority || assignment.resource_type" class="flex items-center gap-1.5 text-[11px] font-mono uppercase text-on-surface-variant min-w-0 overflow-hidden">
        <span
          v-if="assignment.priority"
          :class="{
            'text-error': assignment.priority === 'HIGH',
            'text-amber-600': assignment.priority === 'MEDIUM',
            'text-green-600': assignment.priority === 'LOW',
          }"
        >{{ assignment.priority }}</span>
        <span v-if="assignment.priority && assignment.resource_type" class="text-outline-variant">·</span>
        <span v-if="assignment.resource_type" class="truncate">{{ assignment.resource_type }}</span>
      </div>
      <p v-if="assignment.description" class="text-[11px] text-on-surface-variant line-clamp-1 @[220px]:line-clamp-2">
        {{ assignment.description }}
      </p>
      
    </div>

    <!-- Teams section -->
    <div class="border-t border-border px-2.5 pt-2 pb-2" @click.stop>
      <div class="flex items-center gap-2">
        <span class="text-[10px] font-mono uppercase tracking-widest text-outline shrink-0 hidden @[190px]:inline">Teams</span>

        <draggable
          :model-value="assignedTeams"
          :group="{ name: 'team', put: canEdit, pull: canEdit }"
          item-key="team_id"
          ghost-class="drag-ghost--card"
          drag-class="drag-cursor--chip"
          :force-fallback="true"
          :fallback-on-body="true"
          class="flex flex-wrap items-center gap-1 flex-1 rounded-sm min-h-[20px] border border-dashed transition-colors"
          :class="isDragging ? 'border-primary/50 bg-primary/5' : 'border-transparent'"
          @change="handleChange"
        >
        <template #item="{ element: team }">
          <div class="flex items-center justify-between">
            <TeamTooltip :team="team">
              <span class="inline-flex items-center gap-1 px-1 py-0.5 text-xs bg-surface-container border border-border text-on-surface-variant rounded-sm cursor-grab active:cursor-grabbing">
                <span class="font-mono">{{ team.team_identifier }}
                  <PermissionGuard v-if="assignment.assignment_status !== AssignmentStatus.Completed" action="assignment.create" :role="permissionRole">
                    <button
                      class="text-xs text-outline hover:text-error transition-colors"
                      title="Remove team"
                      @click.stop="emit('detachTeam', team.team_id)"
                    >
                      <span class="material-symbols-outlined !text-[14px]">close</span>
                    </button>
                  </PermissionGuard>
                </span>
              </span>
            </TeamTooltip>
            
          </div>
        </template>
        <template #footer>
          <span
            v-if="assignedTeams.length === 0"
            class="text-[10px] font-mono"
            :class="isDragging ? 'text-primary/60' : 'text-outline-variant'"
          >
            {{ isDragging && canEdit ? 'Drop team here' : 'No teams assigned' }}
          </span>
        </template>
        </draggable>
      </div>
    </div>
  </div>
</template>
